import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { formatValue, getColors, resolveBarKeys } from '../chartUtils';
import type { ChartConfig } from '../chartUtils';
import { TOOLTIP_STYLE, GRID_STROKE, AXIS_TICK } from './constants';
import { buildColorMap } from './buildColorMap';

export default function BarChartView({
  data,
  config,
  isHorizontal,
  fullscreen = false,
}: {
  data: Record<string, unknown>[];
  config: ChartConfig;
  isHorizontal: boolean;
  fullscreen?: boolean;
}) {
  const { categoryKey, valueKey } = resolveBarKeys(config, data);
  const colors = getColors(config);
  const needsRotation = !isHorizontal && data.length > 5;
  const chartHeight = fullscreen ? '100%' : (config.height || Math.max(300, isHorizontal ? data.length * 32 + 60 : 300));

  // Color-by-key: each bar colored by a categorical column
  const colorMap = useMemo(
    () => (config.color_by_key ? buildColorMap(data, config.color_by_key, colors) : null),
    [data, config.color_by_key, colors],
  );

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={data}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        margin={
          isHorizontal
            ? { top: 5, right: 40, left: 120, bottom: config.total_annotation ? 30 : 5 }
            : { top: 5, right: 20, left: 10, bottom: needsRotation ? 80 : config.total_annotation ? 60 : 40 }
        }
      >
        {config.show_grid !== false && <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />}

        {isHorizontal ? (
          <>
            <XAxis
              type="number"
              tick={AXIS_TICK}
              tickFormatter={(v) => formatValue(v, config)}
              domain={[config.min_value ?? 'auto', config.max_value ?? 'auto']}
            />
            <YAxis type="category" dataKey={categoryKey} tick={AXIS_TICK} width={110} />
          </>
        ) : (
          <>
            <XAxis
              dataKey={categoryKey}
              tick={AXIS_TICK}
              angle={needsRotation ? -35 : 0}
              textAnchor={needsRotation ? 'end' : 'middle'}
              height={needsRotation ? 80 : 40}
              interval={0}
              label={
                config.x_label
                  ? { value: config.x_label, position: 'insideBottom', offset: -5, fill: '#888', fontSize: 12 }
                  : undefined
              }
            />
            <YAxis
              tick={AXIS_TICK}
              tickFormatter={(v) => formatValue(v, config)}
              domain={[config.min_value ?? 'auto', config.max_value ?? 'auto']}
              label={
                config.y_label
                  ? { value: config.y_label, angle: -90, position: 'insideLeft', fill: '#888', fontSize: 12 }
                  : undefined
              }
            />
          </>
        )}

        <Tooltip {...TOOLTIP_STYLE} formatter={(value: unknown) => formatValue(value, config)} />
        {config.show_legend && <Legend wrapperStyle={{ fontSize: '12px', color: '#999' }} />}

        <Bar
          dataKey={valueKey}
          fill={colors[0]}
          radius={isHorizontal ? ([0, 4, 4, 0] as unknown as number) : ([4, 4, 0, 0] as unknown as number)}
          name={config.y_label || valueKey}
        >
          {data.map((row, i) => (
            <Cell
              key={i}
              fill={
                colorMap
                  ? colorMap.get(String(row[config.color_by_key!] ?? '')) || colors[0]
                  : colors[i % colors.length]
              }
            />
          ))}
          {config.show_values && (
            <LabelList
              dataKey={valueKey}
              position={isHorizontal ? 'right' : 'top'}
              formatter={(v: unknown) => formatValue(v, config)}
              style={{ fill: '#ccc', fontSize: 10 }}
            />
          )}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
